/**
 * Map tool column — spec §F5.10 ("Map tools (right column, three groups)"):
 *
 * * **Group 1** basemap (toggles the §F5.10 panel of 4 thumb rows) · home (fit programme
 *   bounds);
 * * **Group 2** measure · draw AOI · swipe — toggles, at most one of measure/draw at a
 *   time (the mockup's `stopModes`), and swipe mutually exclusive with the AOI clip (§G6);
 * * **Group 3** analysis toolbox (§F6 — M4 Task 10, live: the button toggles the dock,
 *   which `features/map/index.tsx` renders) · export map (§F5.10's panel — M4 Task 9)
 *   · share (§F1 hash-state permalink → clipboard + toast).
 *
 * Geometry, ids, icons and tooltips are the mockup's `#maptools`. This component also owns
 * the two things the column switches on: `useAoiClip` (§G6's `clip-path` + re-projection)
 * and the `Measure`/`DrawAoi`/`Swipe` behaviours, which render no markup of their own
 * beyond the swipe handle.
 *
 * The AOI chip below the groups is not in the mockup: §F5.10 hands Clip/Release to the
 * "AOI insights & clip" dock and §K M3's acceptance line is "clip masks and releases". The
 * chip shipped in M3 as the minimum control that made the drawn AOI actionable (NOTES
 * #310); it stays now that the dock is here, because it is one click from the map and the
 * dock's own Clip button is three (open · card · scroll) — both write `setAoiClip`.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, type IconName } from '../../lib/icons';
import { copyText } from '../../lib/clipboard';
import { fmtKm2 } from '../../lib/format';
import { shareHash } from '../../lib/permalink';
import { toast } from '../../lib/toast';
import { COUNTRY_NAMES, swipeBlockedByScope, useAppStore } from '../../lib/state';
import { useAoiClip } from './AoiClip';
import { BasemapPanel } from './BasemapPanel';
import { DrawAoi } from './DrawAoi';
import { ExportModal } from './ExportModal';
import { LayerManager } from './LayerManager';
import { Measure } from './Measure';
import { Swipe } from './Swipe';
import { useMap } from './mapContext';
import { fitTo, HOME_BOUNDS } from './panes';

/** The two exclusive draw-style tools; `null` = pointer. */
export type MapTool = 'measure' | 'draw' | null;

export interface MapToolsProps {
  /**
   * §F5.10 "share (hash-state permalink → clipboard + toast)". Defaults to `shareView`
   * below; injectable so a test (or the M4 export modal, which will offer the same link)
   * can drive the column without a clipboard.
   */
  onShare?: () => void;
}

interface ToolBtnProps {
  id: string;
  icon: IconName;
  label: string;
  active?: boolean;
  onClick: () => void;
}

function ToolBtn({ id, icon, label, active = false, onClick }: ToolBtnProps) {
  return (
    <button
      type="button"
      id={id}
      title={label}
      aria-label={label}
      aria-pressed={active}
      className={active ? 'active' : ''}
      onClick={onClick}
    >
      <Icon name={icon} size={17} />
    </button>
  );
}

export function MapTools({ onShare }: MapToolsProps) {
  const { t } = useTranslation();
  const map = useMap();
  const [tool, setTool] = useState<MapTool>(null);
  const [bmOpen, setBmOpen] = useState(false);
  // R8. Local, like `bmOpen`: which floating tool panels are open is this column's business
  // and nothing else reads it — it is not shared, not permalinked, and not §F1 state.
  const [lmOpen, setLmOpen] = useState(false);
  // §F5.10 group 3's export panel (M4 Task 9). Local for the same reason, with one extra:
  // it holds a running job, so closing it is the reader's decision and not a side effect of
  // anything they do to the map behind it.
  const [exportOpen, setExportOpen] = useState(false);

  const aoi = useAppStore((s) => s.aoi);
  const aoiClip = useAppStore((s) => s.aoiClip);
  const analysisOpen = useAppStore((s) => s.analysisOpen);
  const setAnalysisOpen = useAppStore((s) => s.setAnalysisOpen);
  const swipe = useAppStore((s) => s.swipe);
  /** E4: only for the refusal message — the mask itself is `useScopeClip`'s. */
  const scope = useAppStore((s) => s.scope);
  /** …and its round 5 half: with no scope, the country rung is what is holding the pane. */
  const country = useAppStore((s) => s.country);
  const setAoi = useAppStore((s) => s.setAoi);
  const setAoiClip = useAppStore((s) => s.setAoiClip);
  const setSwipe = useAppStore((s) => s.setSwipe);

  useAoiClip(map);

  /** §F5.10 "re-click clears + releases clip"; entering the tool starts a fresh AOI too. */
  const toggleDraw = () => {
    setAoi(null); // also releases the clip (`setAoi`)
    setTool(tool === 'draw' ? null : 'draw');
  };

  /**
   * §G6 "activating either pauses the other with toast". Only *one* toast fires: when the
   * exclusion actually bites, the reason the other tool just went away is the message worth
   * having, and §F2's toast is a single instance — raising the usage hint straight after
   * would overwrite it before it could be read (the mockup did exactly that). NOTES #309.
   */
  const toggleSwipe = () => {
    if (swipe) {
      setSwipe(false);
      return;
    }
    // E4: the scope mask outranks swipe, and `setSwipe` refuses silently — a store action has
    // no `t()`. This is the sentence that refusal is worth: the reader pressed a button and
    // the map did not change, so it has to say what is holding the pane and how to get it
    // back. `swipeBlockedByScope`, not `scopeMaskActive`, and for the reason it carries: this
    // press would release an AOI clip, so the mask that matters is the one the press *leads
    // to*. Reading it the other way let a filter → Clip → Swipe sequence past the rule.
    if (swipeBlockedByScope(useAppStore.getState())) {
      // The name is the filter the reader can see in the cascade: the scoped unit, or — round
      // 5, where the filter is the country box alone — the country. Never the empty string
      // that a country-level filter used to leave in the middle of the sentence.
      const name = scope?.name ?? t(`country.${country}`, COUNTRY_NAMES[country]);
      toast(t('tools.swipeScopeMasked', { name }));
      return;
    }
    const paused = aoiClip;
    setSwipe(true);
    toast(paused ? t('tools.clipReleasedForSwipe') : t('tools.swipeHint'));
  };

  const toggleClip = () => {
    if (aoiClip) {
      setAoiClip(false);
      toast(t('tools.clipReleased'));
      return;
    }
    if (!aoi) return;
    const paused = swipe;
    setAoiClip(true);
    toast(paused ? t('tools.swipePausedForClip') : t('tools.clipOn'));
  };

  /**
   * §F5.10 group 3 / §F1: write the current view into the address bar and put the link on
   * the clipboard.
   *
   * Two deliberate departures from the mockup's three lines (`location.hash = …;
   * navigator.clipboard?.writeText(location.href)`):
   *
   * 1. **`history.replaceState`, not `location.hash =`.** An assignment pushes a history
   *    entry, so a reader who shared three times had to press Back three times to leave the
   *    map. Share records the state; it does not navigate. (Nothing writes the hash on
   *    ordinary state changes either — §F1 says "on Share", and a hash that trailed every
   *    slider drag would bury the real history under hundreds of entries.)
   * 2. **The copy is confirmed.** `writeText` rejects on an insecure origin or an unfocused
   *    document, and the mockup's `.catch(() => {})` still claimed success; `copyText`
   *    falls back and the toast says which of the two actually happened — the link is in
   *    the address bar either way, which is the fallback worth naming.
   */
  const shareView = async () => {
    const hash = shareHash(useAppStore.getState(), window.location.pathname);
    const { origin, pathname, search } = window.location;
    // The address bar keeps `?demo=1` (§F3) so a reload does not silently leave demo mode;
    // the *copied* link drops it — see `shareUrl`.
    window.history.replaceState(null, '', `${pathname}${search}${hash}`);
    const copied = await copyText(`${origin}${pathname}${hash}`);
    toast(t(copied ? 'tools.shareCopied' : 'tools.shareInUrl'));
  };

  return (
    <>
      <div id="maptools">
        <div className="group">
          <ToolBtn
            id="btnBasemap"
            icon="basemap"
            label={t('tools.basemaps')}
            active={bmOpen}
            onClick={() => setBmOpen((o) => !o)}
          />
          {/* R8, narrowed by C1: the five §F5.8 boundary toggles, one click from the map —
              group 1 with the other two "what am I looking at" controls, rather than group
              3's "do something with it". */}
          <ToolBtn
            id="btnLayerMgr"
            icon="globe"
            label={t('boundaries.title')}
            active={lmOpen}
            onClick={() => setLmOpen((o) => !o)}
          />
          <ToolBtn
            id="btnHome"
            icon="home"
            label={t('tools.home')}
            // `fitTo` (not `fitBounds`) — it calls `map.stop()` first, without which a
            // home click landing mid-animation is silently discarded (NOTES #312). Every
            // programmatic fit in the app goes through it; see `panes.fitTo`.
            onClick={() => fitTo(map, HOME_BOUNDS, false)}
          />
        </div>

        <div className="group">
          <ToolBtn
            id="btnMeasure"
            icon="measure"
            label={t('tools.measure')}
            active={tool === 'measure'}
            onClick={() => setTool(tool === 'measure' ? null : 'measure')}
          />
          <ToolBtn
            id="btnDraw"
            icon="draw"
            label={t('tools.draw')}
            active={tool === 'draw'}
            onClick={toggleDraw}
          />
          <ToolBtn
            id="btnSwipe"
            icon="swipe"
            label={t('tools.swipe')}
            active={swipe}
            onClick={toggleSwipe}
          />
        </div>

        <div className="group">
          <ToolBtn
            id="btnAnalysis"
            icon="wrench"
            label={t('tools.analysis')}
            active={analysisOpen}
            // The dock renders inside `MapView` (see `features/map/index.tsx`), not here:
            // this is the switch, and the §F3 topbar's "Analysis" item is the other one,
            // which is why the flag lives in the store rather than in this column.
            onClick={() => setAnalysisOpen(!analysisOpen)}
          />
          <ToolBtn
            id="btnExportMap"
            icon="download"
            label={t('tools.exportMap')}
            active={exportOpen}
            onClick={() => setExportOpen((o) => !o)}
          />
          <ToolBtn
            id="btnShare"
            icon="share"
            label={t('tools.share')}
            onClick={
              onShare ??
              (() => {
                void shareView();
              })
            }
          />
        </div>

        {aoi ? (
          <div id="aoiChip">
            <span className="aoi-area">
              {t('tools.aoiLabel')} · <b>{fmtKm2(aoi.areaKm2)}</b>
            </span>
            <div className="aoi-actions">
              <button type="button" id="btnAoiClip" className="btn primary sm" onClick={toggleClip}>
                {aoiClip ? t('tools.release') : t('tools.clip')}
              </button>
              <button
                type="button"
                id="btnAoiClear"
                className="btn ghost sm"
                onClick={() => {
                  setAoi(null);
                  setTool((cur) => (cur === 'draw' ? null : cur));
                }}
              >
                {t('tools.clearAoi')}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {bmOpen ? <BasemapPanel onClose={() => setBmOpen(false)} /> : null}
      {lmOpen ? <LayerManager onClose={() => setLmOpen(false)} /> : null}
      {exportOpen ? <ExportModal onClose={() => setExportOpen(false)} /> : null}
      <Measure active={tool === 'measure'} onExit={() => setTool(null)} />
      <DrawAoi active={tool === 'draw'} onExit={() => setTool(null)} />
      <Swipe />
    </>
  );
}

export default MapTools;
